import React, { useState, useEffect } from 'react';
import { 
  X, Search, Calendar, Clock, Users, ChevronLeft, ChevronRight, 
  Loader2, Filter, CheckCircle, Clock as ClockIcon, 
  Lock,  FileSpreadsheet, Printer, Edit3, Eye, 
  GraduationCap, BookOpen, UserCheck, UserX,  BarChart3
} from 'lucide-react';
import api from '../../api';
import moment from 'moment';

// Utility to format time in 12-hour format with AM/PM
function formatTime12Hour(timeStr) {
  return moment(timeStr, ['HH:mm', 'H:mm']).format('hh:mm A');
}

// Session Status Badge Component
const SessionStatusBadge = ({ status, daysRemaining, editRemainingMinutes }) => {
  const getStatusConfig = () => {
    if (status === 'marked-editable') {
      return {
        icon: Edit3,
        text: `Editable (${editRemainingMinutes}m)`,
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200',
        dotColor: 'bg-blue-500'
      };
    }
    if (status === 'marked') {
      return {
        icon: CheckCircle,
        text: 'Marked',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200',
        dotColor: 'bg-green-500'
      };
    }
    if (status === 'marked-locked') {
      return {
        icon: Lock,
        text: 'Locked',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200',
        dotColor: 'bg-red-500'
      };
    }
    if (status === 'unmarked') {
      return {
        icon: ClockIcon,
        text: 'Pending',
        bgColor: 'bg-amber-50',
        textColor: 'text-amber-700',
        borderColor: 'border-amber-200',
        dotColor: 'bg-amber-500'
      };
    }
    if (status === 'locked') {
      return {
        icon: Lock,
        text: 'Locked',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200',
        dotColor: 'bg-red-500'
      };
    }
    return {
      icon: ClockIcon,
      text: 'Unknown',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200',
      dotColor: 'bg-gray-500'
    };
  };
  
  const config = getStatusConfig();
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor} border ${config.borderColor}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`}></div>
      <Icon className="w-3.5 h-3.5" />
      <span>{config.text}</span>
      {status === 'unmarked' && daysRemaining > 0 && (
        <span className="ml-1 text-xs font-semibold">({daysRemaining}d)</span>
      )}
    </div>
  );
};

// Session Details Modal Component (Responsive Modal/Drawer)
const SessionDetailsModal = ({ session, onClose, onEdit, onMark }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionDetailsCacheRef = React.useRef(new Map());

  const fetchSessionDetails = React.useCallback(async () => {
    if (session?.attendance && Array.isArray(session.attendance) && session.attendance.length > 0) {
      sessionDetailsCacheRef.current.set(session._id, session);
      setStudents(session.attendance);
      setLoading(false);
      return;
    }

    if (sessionDetailsCacheRef.current.has(session?._id)) {
      const cached = sessionDetailsCacheRef.current.get(session._id);
      if (cached?.attendance && Array.isArray(cached.attendance) && cached.attendance.length > 0) {
        setStudents(cached.attendance);
        setLoading(false);
        return;
      }
      sessionDetailsCacheRef.current.delete(session._id);
    }

    try {
      setLoading(true);
      const response = await api.get(`/api/faculty/attendance/session/${session._id}`);
      if (response.data.success) {
        const loadedSession = response.data.data.session;
        sessionDetailsCacheRef.current.set(session._id, loadedSession);
        setStudents(loadedSession.attendance);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchSessionDetails();
  }, [fetchSessionDetails]);
  
  const getStatusBadge = (status) => {
    const badges = {
      present: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      absent: 'bg-rose-50 text-rose-700 border border-rose-200',
      late: 'bg-amber-50 text-amber-700 border border-amber-200',
      'on-duty': 'bg-blue-50 text-blue-700 border border-blue-200',
      medical: 'bg-purple-50 text-purple-700 border border-purple-200',
      excused: 'bg-gray-50 text-gray-700 border border-gray-200'
    };
    return badges[status] || 'bg-gray-50 text-gray-700';
  };
  
  const getStatusIcon = (status) => {
    switch(status) {
      case 'present': return <UserCheck className="w-3.5 h-3.5" />;
      case 'absent': return <UserX className="w-3.5 h-3.5" />;
      default: return null;
    }
  };
  
  const isEditable = session.sessionStatus?.canEdit === true && (session.sessionStatus?.editRemainingMinutes ?? 0) > 0;
  const isUnmarked = session.sessionStatus?.status === 'unmarked';

  const isLab = session.subjectSnapshot?.isLab || session.subjectId?.isLab || session.sessionType === 'lab';
  const batchName = isLab
    ? session.batchName || session.batchId?.batchName || session.batchId?.name || session.batchId || 'N/A'
    : session.batchId?.batchName || session.batchId?.name || session.batchId || '';

  const presentCount = students.filter(s => s.status === 'present').length;
  const absentCount = students.filter(s => s.status === 'absent').length;
  const lateCount = students.filter(s => s.status === 'late').length;
  const onDutyCount = students.filter(s => s.status === 'on-duty').length;
  const medicalCount = students.filter(s => s.status === 'medical').length;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex justify-end md:justify-center items-end md:items-center p-0 md:p-4" onClick={onClose}>
      <div 
        className="bg-white w-full md:max-w-4xl h-[94vh] md:h-auto md:max-h-[88vh] rounded-t-2xl md:rounded-xl flex flex-col shadow-2xl animate-in slide-in-from-bottom duration-300 md:animate-none"
        onClick={e => e.stopPropagation()}
      >
        {/* Drawer Drag Notch Handle for Mobile */}
        <div className="flex justify-center py-2 md:hidden bg-gray-50 rounded-t-2xl border-b border-gray-100">
          <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
        </div>

        {/* Header */}
        <div className={`px-4 sm:px-5 py-3 border-b ${
          isEditable ? 'bg-blue-600' : isUnmarked ? 'bg-amber-600' : 'bg-emerald-600'
        } text-white md:rounded-t-xl flex justify-between items-center`}>
          <div>
            <h3 className="text-base md:text-lg font-bold">Session Details</h3>
            <p className="text-white/75 text-[11px] mt-0.5">Complete attendance snapshot</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scroller Area */}
        <div className="p-3 sm:p-4 overflow-y-auto flex-1 bg-gray-50/40">
          {/* Quick Meta Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <div className="bg-white border border-gray-100 rounded-lg p-2.5 shadow-sm flex flex-col justify-between">
              <span className="text-[9px] font-bold tracking-wider text-blue-600 uppercase mb-1 flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> Subject
              </span>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm line-clamp-1">{session.subjectSnapshot?.subjectName || session.subjectId?.name}</p>
              <p className="text-[11px] text-gray-500 font-mono mt-1">Code: {session.subjectSnapshot?.subjectCode || session.subjectId?.subjectCode || 'N/A'}</p>
            </div>
            
            <div className="bg-white border border-gray-100 rounded-lg p-2.5 shadow-sm flex flex-col justify-between">
              <span className="text-[9px] font-bold tracking-wider text-purple-600 uppercase mb-1 flex items-center gap-1">
                <GraduationCap className="w-3 h-3" /> Class Group
              </span>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm line-clamp-1">{session.className}</p>
              <div className="flex gap-1.5 flex-wrap mt-1">
                {isLab && <span className="text-[9px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">Batch: {batchName}</span>}
                <span className="text-[9px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded capitalize">
                  {session.electiveGroupName || session.groupName || (session.sessionType && session.sessionType !== 'elective' ? session.sessionType : (isLab ? 'Lab' : 'Theory'))}
                </span>
              </div>
            </div>
            
            <div className="bg-white border border-gray-100 rounded-lg p-2.5 shadow-sm flex flex-col justify-between">
              <span className="text-[9px] font-bold tracking-wider text-green-600 uppercase mb-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Timings
              </span>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm">{moment(session.sessionDate).format('DD MMM YYYY')}</p>
              <p className="text-[11px] text-gray-600 mt-1 font-medium">{formatTime12Hour(session.startTime)} - {formatTime12Hour(session.endTime)}</p>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm mb-3">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-600" />
                <h4 className="font-semibold text-gray-800 text-xs sm:text-sm">Attendance Distribution</h4>
              </div>
              <span className="text-base sm:text-lg font-black text-indigo-600">{session.statistics?.attendancePercentage || 0}%</span>
            </div>
            
            <div className="grid grid-cols-5 gap-1">
              <div className="text-center p-1 bg-emerald-50/50 rounded-md border border-emerald-100/50">
                <div className="text-xs font-bold text-emerald-600">{presentCount}</div>
                <div className="text-[8px] font-medium text-gray-500 uppercase tracking-tight">Pres</div>
              </div>
              <div className="text-center p-1 bg-rose-50/50 rounded-md border border-rose-100/50">
                <div className="text-xs font-bold text-rose-600">{absentCount}</div>
                <div className="text-[8px] font-medium text-gray-500 uppercase tracking-tight">Abs</div>
              </div>
              <div className="text-center p-1 bg-amber-50/50 rounded-md border border-amber-100/50">
                <div className="text-xs font-bold text-amber-600">{lateCount}</div>
                <div className="text-[8px] font-medium text-gray-500 uppercase tracking-tight">Late</div>
              </div>
              <div className="text-center p-1 bg-blue-50/50 rounded-md border border-blue-100/50">
                <div className="text-xs font-bold text-blue-600">{onDutyCount}</div>
                <div className="text-[8px] font-medium text-gray-500 uppercase tracking-tight">OD</div>
              </div>
              <div className="text-center p-1 bg-purple-50/50 rounded-md border border-purple-100/50">
                <div className="text-xs font-bold text-purple-600">{medicalCount}</div>
                <div className="text-[8px] font-medium text-gray-500 uppercase tracking-tight">Med</div>
              </div>
            </div>
          </div>

          {/* Student Ledger Grid / Table */}
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
            <div className="p-3 border-b border-gray-100 bg-gray-50/70 flex items-center justify-between">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2 text-xs uppercase tracking-wider">
                <Users className="w-4 h-4 text-gray-400" /> Roster ({students.length})
              </h4>
            </div>

            {loading ? (
              <div className="text-center py-10">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
                <p className="text-xs text-gray-400 mt-2">Fetching records...</p>
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-xs">No records tracked</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[40vh] overflow-y-auto">
                {/* Desktop View Table Header */}
                <div className="hidden md:grid grid-cols-12 gap-2 p-3 bg-gray-50 text-xs font-bold text-gray-500 uppercase">
                  <div className="col-span-1">#</div>
                  <div className="col-span-6">Student</div>
                  <div className="col-span-3">Identifier</div>
                  <div className="col-span-2 text-right">Status</div>
                </div>

                {students.map((student, idx) => (
                  <div key={idx} className="p-3 md:grid md:grid-cols-12 md:gap-2 items-center flex justify-between hover:bg-gray-50/80 transition-colors">
                    {/* Index & Student Profile details */}
                    <div className="col-span-7 flex items-center gap-3">
                      <span className="text-xs font-mono text-gray-400 hidden md:inline-block w-4">{idx + 1}</span>
                      <div className="flex items-center">
                        {(() => {
                          const backendUrl = api.defaults.baseURL || 'http://localhost:5000';
                          const profileUrl = student.studentDetails?.profilePhotoUrl;
                          const name = student.studentDetails?.name || 'Unknown';
                          const showImage = profileUrl && typeof profileUrl === 'string' && !profileUrl.endsWith('default-profile.png');
                          
                          if (showImage) {
                            const imgSrc = `${backendUrl}${profileUrl.startsWith('/') ? profileUrl : '/' + profileUrl}`;
                            return (
                              <img
                                src={imgSrc}
                                alt={name}
                                className="w-9 h-9 md:w-8 md:h-8 rounded-full object-cover border border-gray-100 bg-white"
                                onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                              />
                            );
                          } else {
                            const initial = name.trim().charAt(0).toUpperCase();
                            return (
                              <span className="w-9 h-9 md:w-8 md:h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 font-bold text-xs">
                                {initial}
                              </span>
                            );
                          }
                        })()}
                        <div className="ml-2.5 text-left">
                          <p className="font-semibold text-gray-800 text-sm line-clamp-1">{student.studentDetails?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-400 font-mono md:hidden mt-0.5">
                            {student.studentDetails?.USN || student.studentDetails?.rollNumber || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Desktop specific columns */}
                    <div className="col-span-3 text-xs font-mono text-gray-500 hidden md:block">
                      {student.studentDetails?.USN || student.studentDetails?.rollNumber || 'N/A'}
                    </div>

                    {/* Status badges */}
                    <div className="col-span-2 md:text-right">
                      <span className={`inline-flex items-center gap-1 capitalize px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBadge(student.status)}`}>
                        {getStatusIcon(student.status)}
                        {student.status || 'Not marked'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Panel Footer */}
        <div className="p-4 border-t bg-gray-50 flex flex-col-reverse sm:flex-row justify-end gap-2 shrink-0 pb-6 sm:pb-4">
          <button onClick={onClose} className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-xl bg-white hover:bg-gray-50 transition text-gray-700 text-sm font-semibold">
            Close View
          </button>
          {isUnmarked && (
            <button 
              onClick={onMark} 
              className="w-full sm:w-auto px-4 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 shadow-md shadow-amber-600/10 transition flex items-center justify-center gap-2 text-sm font-semibold"
            >
              <CheckCircle className="w-4 h-4" /> Mark Attendance
            </button>
          )}
          {isEditable && (
            <button 
              onClick={onEdit} 
              className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-md shadow-blue-600/10 transition flex items-center justify-center gap-2 text-sm font-semibold"
            >
              <Edit3 className="w-4 h-4" /> Modify Attendance
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Attendance History Modal View
const AttendanceHistoryModal = ({ isOpen, onClose, facultyId, onEditAttendance, onMarkAttendance }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailedSession, setDetailedSession] = useState(null);
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  
  const itemsPerPage = 10;

  const fetchSessions = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/faculty/${facultyId}/attendance/all-with-status`);
      
      if (response.data.success) {
        setSessions(response.data.data.sessions);
        setSummary(response.data.data.summary);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [facultyId]);

  const fetchFilters = React.useCallback(async () => {
    try {
      const response = await api.get(`/api/faculty/${facultyId}/attendance/summary`);
      if (response.data.success) {
        const data = response.data.data;
        
        const uniqueClasses = new Map();
        const uniqueSubjects = new Map();
        
        data.sessions?.forEach(session => {
          if (session.classId?._id && !uniqueClasses.has(session.classId._id)) {
            uniqueClasses.set(session.classId._id, {
              id: session.classId._id,
              name: session.className || session.classId?.className
            });
          }
          if (session.subjectId?._id && !uniqueSubjects.has(session.subjectId._id)) {
            uniqueSubjects.set(session.subjectId._id, {
              id: session.subjectId._id,
              name: session.subjectSnapshot?.subjectName || session.subjectId?.name
            });
          }
        });
        
        setClasses(Array.from(uniqueClasses.values()));
        setSubjects(Array.from(uniqueSubjects.values()));
      }
    } catch (error) {
    }
  }, [facultyId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterClass, filterSubject, filterStatus, dateRange.start, dateRange.end]);

  useEffect(() => {
    if (isOpen && facultyId) {
      fetchSessions();
      fetchFilters();
    }
  }, [isOpen, facultyId, fetchSessions, fetchFilters]);

  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    setShowSessionDetails(true);
    setDetailedSession(session);
  };

  const handleMarkSession = (session) => {
    setShowSessionDetails(false);
    setSelectedSession(null);
    setDetailedSession(null);
    if (onMarkAttendance) {
      onMarkAttendance(session);
    } else if (onEditAttendance) {
      onEditAttendance(session);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (filterClass) params.append('classId', filterClass);
      if (filterSubject) params.append('subjectId', filterSubject);
      if (filterStatus !== 'all') params.append('statusFilter', filterStatus);
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);
      
      const response = await api.get(`/api/faculty/${facultyId}/attendance/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
    } finally {
      setExporting(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    const searchLower = searchTerm.toLowerCase();
    const sessionDateValue = moment(session.sessionDate).format('YYYY-MM-DD');
    const sessionStatusValue = (() => {
      if (session.attendanceMarkingStatus === 'completed') {
        return session.isLocked ? 'marked-locked' : 'marked';
      }
      if ((session.isBlocked || session.isLocked) && !session.isUnblockedByAdmin) {
        return 'locked';
      }
      if (session.sessionStatus?.status === 'marked-editable') {
        return 'marked-editable';
      }
      return 'unmarked';
    })();

    const matchesSearch =
      session.className?.toLowerCase().includes(searchLower) ||
      session.subjectSnapshot?.subjectName?.toLowerCase().includes(searchLower) ||
      session.subjectId?.name?.toLowerCase().includes(searchLower);

    const matchesClass = !filterClass || session.classId?._id === filterClass || session.classId === filterClass;
    const matchesSubject = !filterSubject || session.subjectId?._id === filterSubject || session.subjectId === filterSubject;
    const matchesStatus = filterStatus === 'all' || sessionStatusValue === filterStatus;
    const matchesStartDate = !dateRange.start || sessionDateValue >= dateRange.start;
    const matchesEndDate = !dateRange.end || sessionDateValue <= dateRange.end;

    return matchesSearch && matchesClass && matchesSubject && matchesStatus && matchesStartDate && matchesEndDate;
  });

  const totalPagesFromFiltered = Math.max(1, Math.ceil(filteredSessions.length / itemsPerPage));

  const getPaginatedSessions = () => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredSessions.slice(start, end);
  };

  const statusFilterOptions = [
    { value: 'all', label: 'All', icon: Calendar, color: 'bg-gray-100 text-gray-700' },
    { value: 'marked', label: 'Marked', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
    { value: 'editable', label: 'Editable', icon: Edit3, color: 'bg-blue-100 text-blue-700' },
    { value: 'unmarked', label: 'Pending', icon: ClockIcon, color: 'bg-amber-100 text-amber-700' },
    { value: 'locked', label: 'Locked', icon: Lock, color: 'bg-rose-100 text-rose-700' }
  ];

  const paginatedSessions = getPaginatedSessions();

  const pendingEligibleSessions = paginatedSessions.filter(
    s =>
      s.sessionStatus?.status === 'unmarked' &&
      (s.sessionStatus?.canMark === true ||
       s.sessionStatus?.canEdit === true ||
       s.isUnblockedByAdmin === true)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end md:justify-center items-end md:items-center p-0 md:p-4" onClick={onClose}>
      <div 
        className="bg-gray-50 md:bg-white w-full md:max-w-6xl h-[95vh] md:h-[90vh] rounded-t-2xl md:rounded-xl flex flex-col shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300 md:animate-none"
        onClick={e => e.stopPropagation()}
      >
        {/* Mobile Top Drag Indicator Handle */}
        <div className="flex justify-center py-2 md:hidden bg-indigo-700 shrink-0">
          <div className="w-12 h-1 bg-white/30 rounded-full"></div>
        </div>

        {/* Main Header Banner */}
        <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 text-white shrink-0 shadow-md">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <GraduationCap className="w-5 h-5 text-indigo-200" />
                <h2 className="text-lg md:text-xl font-bold truncate">Attendance Logs</h2>
              </div>
              <p className="text-white/70 text-xs">Track your academic lecture and practical cycles</p>
              
              {/* Stats Bar - Horizontal Carousel Scroll on Small Displays */}
              {summary && (
                <div className="flex gap-2.5 mt-3.5 overflow-x-auto pb-1 scrollbar-none -mx-4 px-4 md:mx-0 md:px-0">
                  <div className="bg-white/10 backdrop-blur-md border border-white/5 rounded-lg px-2 py-1 min-w-[84px] flex-shrink-0">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-white/60">Total</div>
                    <div className="text-sm font-extrabold leading-none mt-0.5">{summary.total}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md border border-white/5 rounded-lg px-2 py-1 min-w-[84px] flex-shrink-0">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-emerald-300/80">Done</div>
                    <div className="text-sm font-extrabold text-emerald-300 leading-none mt-0.5">{summary.marked}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md border border-white/5 rounded-lg px-2 py-1 min-w-[84px] flex-shrink-0">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-amber-300/80">Pending</div>
                    <div className="text-sm font-extrabold text-amber-300 leading-none mt-0.5">{summary.unmarked}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md border border-white/5 rounded-lg px-2 py-1 min-w-[84px] flex-shrink-0">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-rose-300/80">Locked</div>
                    <div className="text-sm font-extrabold text-rose-300 leading-none mt-0.5">{summary.blocked || 0}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md border border-white/5 rounded-lg px-2 py-1 min-w-[84px] flex-shrink-0">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-indigo-200">Avg</div>
                    <div className="text-sm font-extrabold leading-none mt-0.5">{Math.round(summary.averageAttendance)}%</div>
                  </div>
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors ml-2">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Sticky Action Notification inside Modal */}
        {pendingEligibleSessions.length > 0 && (
          <div className="px-4 pt-3 bg-gray-50/50 shrink-0">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-2.5 min-w-0">
                <ClockIcon className="w-5 h-5 text-amber-600 shrink-0" />
                <div className="min-w-0">
                  <span className="block text-xs font-bold text-amber-900">Unsubmitted Sheets Pending</span>
                  <span className="block text-[11px] text-amber-700 truncate">
                    You have {pendingEligibleSessions.length} session{pendingEligibleSessions.length !== 1 ? 's' : ''} awaiting marking action.
                  </span>
                </div>
              </div>
              <button
                className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 shrink-0 transition flex items-center gap-1 shadow-sm"
                onClick={() => onMarkAttendance(pendingEligibleSessions[0])}
              >
                Mark Now
              </button>
            </div>
          </div>
        )}

        {/* Toolbar Filter & Controllers */}
        <div className="px-4 py-3 border-b bg-white shrink-0 shadow-sm z-10">
          <div className="flex flex-col lg:flex-row gap-2.5 items-stretch lg:items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Filter by class or subject string..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-gray-50/50"
              />
            </div>
            
            {/* Quick utility system controls */}
            <div className="grid grid-cols-3 sm:flex gap-1.5 justify-stretch">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 border rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition ${
                  showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" /> Filters
              </button>
              
              <button
                onClick={handleExport}
                disabled={exporting}
                className="px-3 py-2 bg-emerald-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition flex items-center justify-center gap-1.5 shadow-sm shadow-emerald-600/10"
              >
                <FileSpreadsheet className="w-4 h-4" /> Export
              </button>
              
              <button
                onClick={() => window.print()}
                className="px-3 py-2 bg-gray-700 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition flex items-center justify-center gap-1.5 shadow-sm"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
            </div>
          </div>
          
          {/* Status Select Chip Elements */}
          <div className="flex flex-wrap gap-1.5 mt-3 overflow-x-auto scrollbar-none">
            {statusFilterOptions.map(option => (
              <button
                key={option.value}
                onClick={() => setFilterStatus(option.value)}
                className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wide transition-all shrink-0 flex items-center gap-1 border ${
                  filterStatus === option.value 
                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' 
                    : `${option.color} border-gray-200/80`
                }`}
              >
                <option.icon className="w-3 h-3" />
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Advanced Extended Dropdown Filter Panel */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-100 animate-in fade-in duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <select
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Classes</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
                
                <select
                  value={filterSubject}
                  onChange={(e) => setFilterSubject(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Subjects</option>
                  {subjects.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
                
                <div className="flex flex-col gap-0.5">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-1.5 border border-gray-200 rounded-xl text-sm bg-gray-50 w-full font-mono"
                  />
                </div>
                
                <div className="flex flex-col gap-0.5">
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-1.5 border border-gray-200 rounded-xl text-sm bg-gray-50 w-full font-mono"
                  />
                </div>
              </div>
              
              <div className="mt-2.5 flex justify-end">
                <button
                  onClick={() => {
                    setFilterClass('');
                    setFilterSubject('');
                    setDateRange({ start: '', end: '' });
                    setFilterStatus('all');
                    setSearchTerm('');
                  }}
                  className="text-xs font-bold text-rose-600 hover:bg-rose-50 px-2.5 py-1 rounded-lg transition"
                >
                  Reset Active Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sessions Dynamic Feed List Wrapper */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-100/40">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-2" />
              <p className="text-gray-500 text-sm font-medium">Processing logs registry...</p>
            </div>
          ) : paginatedSessions.length === 0 ? (
            <div className="text-center py-16 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm max-w-sm mx-auto mt-6">
              <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-700 font-bold text-sm">No standard sessions match</p>
              <p className="text-xs text-gray-400 mt-1">Refine options or check historical intervals.</p>
            </div>
          ) : (
            <div className="space-y-3 max-w-5xl mx-auto">
              {paginatedSessions.map((session) => {
                let displayStatus = 'unmarked';
                if (session.attendanceMarkingStatus === 'completed') {
                  displayStatus = session.isLocked ? 'marked-locked' : 'marked';
                } else if ((session.isBlocked || session.isLocked) && !session.isUnblockedByAdmin) {
                  displayStatus = 'locked';
                } else if (session.sessionStatus?.status === 'marked-editable') {
                  displayStatus = 'marked-editable';
                }

                const isUnmarked = session.attendanceMarkingStatus !== 'completed' && (
                  (!session.isLocked && !session.isBlocked) || session.isUnblockedByAdmin
                );
                const isEditable = session.sessionStatus?.canEdit === true && (session.sessionStatus?.editRemainingMinutes ?? 0) > 0;
                const editRemaining = session.sessionStatus?.editRemainingMinutes || 0;
                const isLab = session.sessionType === 'lab';
                const isBlocked = displayStatus === 'locked';

                return (
                  <div
                    key={session._id}
                    className={`bg-white border rounded-xl p-4 hover:shadow-md transition-all duration-200 relative group flex flex-col justify-between ${
                      isUnmarked ? 'border-l-4 border-l-amber-500 border-gray-200' :
                      isEditable ? 'border-l-4 border-l-blue-500 border-gray-200' :
                      'border-gray-200/70 shadow-sm'
                    }`}
                    onClick={() => handleSessionClick(session)}
                  >
                    {/* Header alerts embedded inline inside the cards */}
                    {isBlocked && (
                      <div className="mb-2.5 flex items-center gap-2 p-2 bg-rose-50 border border-rose-100 rounded-lg text-[11px] text-rose-800 font-medium">
                        <Lock className="w-3.5 h-3.5 text-rose-500" /> Blocked — marking limit window breached
                      </div>
                    )}
                    {session.isUnblockedByAdmin && !session.isLocked && (
                      <div className="mb-2.5 flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-100 rounded-lg text-[11px] text-emerald-800 font-medium">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> Exception authorization active
                      </div>
                    )}
                    
                    {/* Main structural rows adaptive layout mapping */}
                    <div className="flex flex-col sm:grid sm:grid-cols-12 gap-2 w-full">
                      <div className="sm:col-span-5 min-w-0">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">Subject Unit</span>
                        <h4 className="font-bold text-gray-900 text-sm truncate">
                          {session.subjectSnapshot?.subjectName || session.subjectId?.name}
                        </h4>
                        <span className="text-xs text-gray-500 font-mono">
                          {session.subjectSnapshot?.subjectCode || session.subjectId?.subjectCode || '—'}
                        </span>
                      </div>

                      <div className="sm:col-span-4 grid grid-cols-2 sm:flex sm:flex-col gap-1 mt-2 sm:mt-0">
                        <div>
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block sm:hidden">Class Group</span>
                          <span className="text-xs text-gray-800 font-semibold flex items-center gap-1">
                            <GraduationCap className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="truncate">{session.className}</span>
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block sm:hidden">Category</span>
                          <span className="inline-block text-[10px] font-bold bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full capitalize">
                            {(() => {
                              if (session.subjectSnapshot?.subjectType === 'custom' || session.subjectId?.subjectType === 'custom') {
                                const customType = session.subjectSnapshot?.customSubjectType || session.subjectId?.customSubjectType;
                                if (customType && customType.toLowerCase() !== 'custom') return customType.charAt(0).toUpperCase() + customType.slice(1);
                                return session.subjectSnapshot?.subjectName || session.subjectId?.name || 'Custom';
                              }
                              return session.electiveGroupName || session.groupName || (session.sessionType && session.sessionType !== 'elective' ? session.sessionType : (isLab ? 'Lab' : 'Theory'));
                            })()}
                          </span>
                        </div>
                      </div>

                      <div className="sm:col-span-3 flex sm:flex-col justify-between sm:justify-start sm:items-end items-center border-t border-gray-100 sm:border-0 pt-2 sm:pt-0 mt-2 sm:mt-0">
                        <div className="text-left sm:text-right">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block sm:hidden">Roster Yield</span>
                          <span className="text-lg font-black text-indigo-600 block leading-tight">
                            {session.statistics?.attendancePercentage || 0}%
                          </span>
                          <span className="text-xs text-gray-500 block font-medium">
                            {session.statistics?.presentCount || 0}/{session.statistics?.totalStudents || 0} Attended
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Meta Footer Row Info */}
                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 bg-gray-50/50 -mx-4 -mb-4 p-3 rounded-b-xl md:mx-0 md:mb-0 md:p-0 md:bg-transparent md:border-t-0">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500 font-medium">
                        <span className="flex items-center gap-1 bg-white md:bg-transparent px-2 py-0.5 md:p-0 rounded border md:border-0 border-gray-200">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          {moment(session.sessionDate).format('DD MMM YYYY')}
                        </span>
                        <span className="flex items-center gap-1 bg-white md:bg-transparent px-2 py-0.5 md:p-0 rounded border md:border-0 border-gray-200">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {formatTime12Hour(session.startTime)}
                        </span>
                        {isLab && (
                          <span className="text-[11px] bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-bold">
                            Batch: {session.batchName || session.batchId?.batchName || session.batchId?.name || 'N/A'}
                          </span>
                        )}
                      </div>

                      {/* Interactive Buttons Adaptive Tray */}
                      <div className="flex items-center gap-2 w-full sm:w-auto justify-end mt-2 sm:mt-0">
                        <SessionStatusBadge 
                          status={displayStatus}
                          daysRemaining={session.sessionStatus?.daysRemaining}
                          editRemainingMinutes={editRemaining}
                        />
                        
                        <div className="flex gap-1.5 ml-1">
                          {isUnmarked && (() => {
                            const now = moment();
                            const sessionDate = moment(session.sessionDate);
                            const [startHour, startMinute] = session.startTime.split(':');
                            sessionDate.set({ hour: Number(startHour), minute: Number(startMinute), second: 0, millisecond: 0 });
                            if (now.isSameOrAfter(sessionDate)) {
                              return (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (onMarkAttendance) onMarkAttendance(session);
                                  }}
                                  className="px-3 py-1.5 bg-amber-600 text-white font-semibold rounded-lg text-xs hover:bg-amber-700 transition flex items-center gap-1 shadow-sm shadow-amber-600/10"
                                >
                                  Mark
                                </button>
                              );
                            } else {
                              return (
                                <button
                                  disabled
                                  className="px-3 py-1.5 bg-gray-200 text-gray-400 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-not-allowed"
                                >
                                  Upcoming
                                </button>
                              );
                            }
                          })()}
                          {isEditable && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                if (onEditAttendance) onEditAttendance(session);
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white font-semibold rounded-lg text-xs hover:bg-blue-700 transition flex items-center gap-1 shadow-sm shadow-blue-600/10"
                            >
                              Edit
                            </button>
                          )}
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleSessionClick(session);
                            }}
                            className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold hover:bg-gray-50 transition flex items-center gap-1 bg-white text-gray-700 shadow-sm"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Linear Micro Indicator Bar Chart */}
                    <div className="w-full bg-gray-100 rounded-full h-1 mt-3 overflow-hidden hidden md:block">
                      <div 
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 h-1 rounded-full transition-all"
                        style={{ width: `${session.statistics?.attendancePercentage || 0}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Unified Bottom Responsive Pagination Tray */}
        {totalPagesFromFiltered > 1 && (
          <div className="px-4 py-3.5 border-t bg-white flex justify-between items-center shrink-0 shadow-md z-10 pb-6 sm:pb-3.5">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed bg-white"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="text-xs font-medium text-gray-500">
              Page <span className="font-bold text-gray-900">{currentPage}</span> of <span className="font-bold text-gray-900">{totalPagesFromFiltered}</span>
            </span>
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPagesFromFiltered, p + 1))}
              disabled={currentPage === totalPagesFromFiltered}
              className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed bg-white"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Subordinate Session Details Modal Router */}
        {showSessionDetails && (detailedSession || selectedSession) && (
          <SessionDetailsModal
            session={detailedSession || selectedSession}
            onClose={() => {
              setShowSessionDetails(false);
              setSelectedSession(null);
              setDetailedSession(null);
            }}
            onEdit={() => {
              setShowSessionDetails(false);
              if (onEditAttendance) onEditAttendance(detailedSession || selectedSession);
            }}
            onMark={() => {
              handleMarkSession(detailedSession || selectedSession);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AttendanceHistoryModal;